/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PageInfo } from 'src/core/utils/pagination/pagination';
import { LuisId } from 'src/luis/types/Resource';

export type ChangePageSizeFactoryParams<T> = {
    pages: Map<number, Map<LuisId, T>>;
    oldPageSize: number;
    newPageSize: number;
    totalSize: number;
    activePageIndex: number;
};

export type ChangePageSizeReturnType = { pageInfo?: PageInfo; activePageIndex: number };

/**
 * Changes the page size of the pages map and returns the info of the map that needs
 * to be refreshed alongside the new active page index.
 */
export const changePageSizeHelper = <T>(params: ChangePageSizeFactoryParams<T>): ChangePageSizeReturnType => {
    const { newPageSize, oldPageSize, pages, totalSize } = params;
    let { activePageIndex } = params;
    const newPagesMap: Map<number, Map<LuisId, T>> = new Map();
    activePageIndex = Math.floor(activePageIndex / (newPageSize / oldPageSize));

    if (newPageSize > oldPageSize) {
        const ratio = newPageSize / oldPageSize;
        for (let i = 0; i < totalSize / newPageSize; i++) {
            const newPage: Map<LuisId, T> = new Map();
            for (let j = 0; j < ratio; j++) {
                const oldPageIndex = i * ratio + j;
                const oldPage = pages.get(oldPageIndex);
                if (oldPage) {
                    oldPage.forEach((val, key) => newPage.set(key, val));
                }
            }
            newPagesMap.set(i, newPage);
        }
    } else {
        const ratio = oldPageSize / newPageSize;
        for (let i = 0; i < totalSize / oldPageSize; i++) {
            const oldPage = pages.get(i);
            if (oldPage) {
                for (let j = 0; j < ratio; j++) {
                    const newPage = [...oldPage.entries()].slice(j * newPageSize, (j + 1) * newPageSize);
                    newPagesMap.set(i * ratio + j, new Map(newPage));
                }
            }
        }
    }

    // Copy new pages into the pages map.
    pages.clear();
    newPagesMap.forEach((val, key) => {
        if (val.size === newPageSize || key === activePageIndex) {
            pages.set(key, val);
        }
    });

    const activePage = pages.get(activePageIndex);
    if (activePage.size !== newPageSize) {
        return { pageInfo: { skip: activePageIndex * newPageSize, take: newPageSize }, activePageIndex };
    }

    return { activePageIndex };
};
